import { curry } from 'lodash';
import styled from '@emotion/styled';
import TextArea from 'react-autosize-textarea';
import React, { KeyboardEvent, useContext, useState } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { FormGroup, FormControl } from 'react-bootstrap';
import ActionButton from '../../Components/ActionButton';

import { assetUrl } from '~/helpers';
import Switch from '~/App/Components/Switch';
import { palette } from '~/utils/colours';
import CtaModal from '../../Components/CtaModal';
import EditorContext, { EditorContextProps } from './Context';
import CharacterLimit from '~/App/Components/CharacterLimit';
import Headline from '../../Components/Headline';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { EmojiSelector } from '@vimily/bonjoro-ui-components';

const FlexDiv = styled.div`
  display: flex;
`;

const PersonalizeLabel = styled.label`
  margin-bottom: 0;
`;

const StyledTooltip = styled(Tooltip)`
  .tooltip-inner {
    max-width: 300px;
    white-space: normal;
    text-align: left;
  }
`;

const Tips = styled.div`
  display: flex;
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0.08px;
  color: ${palette.tealishTwo};
  align-self: center;
`;

const TipsText = styled.span`
  margin-right: 12px;
`;

const StyledTextArea = styled(TextArea)`
  resize: none;
`;

const CtaContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FlexedFormGroup = styled(FormGroup)`
  display: flex;
  justify-content: space-between;
`;

const MergeTagsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const SubjectLineFormGroup = styled(FormGroup)`
  margin-bottom: 20px;
`;

const CTAHeadline = styled(Headline)`
  margin-bottom: 0;
`;

const CustomiseCTAImage = styled.img`
  margin-right: 12px;
`;

const MESSAGE_TEMPLATE_MAX_LENGTH = 300;
const SUBJECT_LINE_MAX_LENGTH = 100;

const handleSubjectLineKeyDown = (evt: React.KeyboardEvent<FormControl>) => {
  if (evt.keyCode === 13) {
    evt.preventDefault();
  }
};

export default function MessageContent() {
  const context: EditorContextProps = useContext(EditorContext);
  const formControlChange = curry(
    (context: EditorContextProps, key: string, evt: KeyboardEvent) =>
      context.set<string>(key, (evt.target as HTMLInputElement).value)
  );
  const { metadata, get } = context;

  const [isCtaModalOpen, setCtaModalOpen] = useState(false);
  const isCtaDisplayed = context.get<boolean>(
    'landing_page.cta.is_displayed',
    true
  );

  const handlers = {
    setSubjectLine: formControlChange(context, 'email_template.subject_line'),
    setMessageTemplate: formControlChange(
      context,
      'email_template.message_template'
    ),
  };

  const TipsHover = (
    <StyledTooltip>
      Merge tags are available in Custom Subject Line and Message Text.
      <br />
      <br />
      You can include tags like &#123;first_name&#125; &#123;last_name&#125;
      &#123;full_name&#125; &#123;email&#125; that correspond with your contacts
      stored data.
      <br />
      <br />
      When names might be missing use the fallback function. For example
      &apos;Hi &#123;first_name|there&#125;&apos; will display as &apos;Hi
      there&apos; when there is an empty name field.
    </StyledTooltip>
  );

  const onCtaDisplayChange = (newValue: boolean) => {
    context.set<boolean>('landing_page.cta.is_displayed', newValue);
  };

  return (
    <>
      <FlexDiv>
        <FormGroup>
          <PersonalizeLabel>
            Personalise your message so your customers take action.
          </PersonalizeLabel>
        </FormGroup>
      </FlexDiv>

      <SubjectLineFormGroup controlId='subject-line'>
        <Headline
          title='Subject line'
          id='subject-line'
          helpIcon={faQuestionCircle}
          helpContent='This is the subject line for the delivery email. Make it recognisable to your recipients.'
        />

        <FormControl
          maxLength={SUBJECT_LINE_MAX_LENGTH}
          componentClass={StyledTextArea}
          value={(get('email_template.subject_line') as string) || ''}
          placeholder={metadata?.defaultSubjectLine}
          onChange={handlers.setSubjectLine}
          onKeyDown={handleSubjectLineKeyDown}
        />
      </SubjectLineFormGroup>

      <FormGroup controlId='message-text' style={{ position: 'relative' }}>
        <Headline
          title='Message text'
          id='message-text'
          helpIcon={faQuestionCircle}
          helpContent='This is the message that will be shown in the delivery email and video landing page. Personalise this to increase engagement.'
        />
        <FormControl
          maxLength={MESSAGE_TEMPLATE_MAX_LENGTH}
          value={(get('email_template.message_template') as string) || ''}
          componentClass={StyledTextArea}
          placeholder={metadata?.defaultMessage}
          onChange={handlers.setMessageTemplate}
          as='textarea'
          rows={8}
          style={{ paddingBottom: '1.5em' }}
        />
        <div style={{ position: 'absolute', left: '5px', bottom: '1px' }}>
          <EmojiSelector
            closeOnSelect
            onSelect={(emoji) =>
              context.set(
                'email_template.message_template',
                `${get('email_template.message_template') as string}${emoji.native}`
              )
            }
            positions={['right', 'top']}
            iconClassName='text-black'
            iconContainerClassName='p-1'
          />
        </div>
        <CharacterLimit
          style={{
            position: 'absolute',
            left: '30px',
            bottom: '2px',
          }}
          limit={MESSAGE_TEMPLATE_MAX_LENGTH}
          input={get('email_template.message_template') || String()}
        />
      </FormGroup>
      <MergeTagsWrapper>
        <OverlayTrigger
          placement='bottom'
          overlay={TipsHover}
          delayShow={250}
          delayHide={400}
        >
          <Tips>
            <TipsText>What are merge tags?</TipsText>
            <img
              src={assetUrl(
                '/assets/images/icons/message-template/TagTips.svg'
              )}
            />
          </Tips>
        </OverlayTrigger>
      </MergeTagsWrapper>

      <FlexedFormGroup>
        <CTAHeadline
          title='Call to action'
          id='call-to-action'
          helpIcon={faQuestionCircle}
          helpContent='Configure the call to action button on the video landing page. Drive recipients to take the next action.'
        />
        <Switch
          value={isCtaDisplayed}
          onClick={onCtaDisplayChange}
          style={{ verticalAlign: 'sub' }}
        />
      </FlexedFormGroup>

      <FormGroup>
        <CtaContainer>
          <ActionButton
            block
            bsStyle='secondary'
            onClick={() => setCtaModalOpen(true)}
            disabled={!isCtaDisplayed}
          >
            <CustomiseCTAImage
              src={assetUrl(
                'assets/images/icons/message-template/customiseCTA.svg'
              )}
            />
            <span>Customise call to action</span>
          </ActionButton>
        </CtaContainer>
      </FormGroup>
      <CtaModal isOpen={isCtaModalOpen} setOpen={setCtaModalOpen} />
    </>
  );
}
